/**
 * Team Pick Adapter
 *
 * Transforms admin preview data into TeamPick component props
 */

import { createTeamsFromOptions } from '@low6-pickem/ui/utils';
import type { PreviewQuestionData } from '../types';

/**
 * TeamPick component props (from packages/ui/src/components/TeamPick.tsx)
 */
interface TeamPickProps {
  questionId?: string;
  questionText: string;
  subtext?: string;
  teams: [any, any]; // Tuple of exactly 2 teams
  selectedTeam?: string;
  onTeamSelect: (teamId: string | undefined) => void;
  points: number;
  earnedPoints?: number;
  isCorrect?: boolean;
  disabled?: boolean;
  className?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

/**
 * Transforms preview data into TeamPick props
 *
 * Creates Team objects from the first two options and returns props
 * configured for preview mode (disabled, no selection, no handlers).
 *
 * @param data - Preview question data from admin form
 * @returns TeamPick component props
 */
export function teamPickAdapter(data: PreviewQuestionData): TeamPickProps {
  const language = data.language || 'en';

  // Create Team objects from first two options
  const teams = createTeamsFromOptions(data.options.slice(0, 2), language);

  // Ensure we have exactly 2 teams
  if (teams.length < 2) {
    throw new Error('TeamPick requires at least 2 options');
  }

  return {
    questionId: 'preview',
    questionText: data.questionText,
    subtext: data.questionSubtext,
    teams: [teams[0], teams[1]], // Exactly 2 teams as tuple
    selectedTeam: undefined, // No selection in preview
    onTeamSelect: () => {}, // No-op handler (preview is view-only)
    points: data.totalPoints,
    disabled: true, // Preview is view-only
    questionNumber: undefined, // No question number in preview
  };
}
